'use client'

import Link from 'next/link'

import { KineticWorkshopFooter } from '../../../components/kinetic/KineticWorkshopFooter'
import { Button } from '../../../components/ui/button'

const curriculum = [
  {
    day: 'Day 1: Developer Portal Foundation',
    topics: [
      'Developer portal concepts',
      'Backstage.io architecture',
      'Core concepts: Catalog, TechDocs',
      'First Backstage instance setup',
    ],
  },
  {
    day: 'Day 2: Data Ingestion',
    topics: [
      'Custom Software Templates',
      'Standardised Workflows',
      'Version control integration',
      'Template testing and validation',
    ],
  },
  {
    day: 'Day 3: Documentation as Code',
    topics: [
      'TechDocs setup & configuration',
      'Documentation templates',
      'API documentation automation',
      'CI/CD integration',
    ],
  },
  {
    day: 'Day 4: Product Lifecycle',
    topics: [
      'Custom plugins development',
      'Quality gates implementation',
      'Monitoring integration',
      'Data quality dashboards',
    ],
  },
  {
    day: 'Day 5: Production & Scale',
    topics: [
      'Production deployment',
      'High availability setup',
      'Security best practices',
      'Cost optimisation',
    ],
  },
]

const benefits = [
  'Build and manage scalable data product platforms',
  'Implement developer portals with Backstage.io',
  'Create standardised workflows and golden paths',
  'Drive innovation and team productivity',
]

const features = [
  { title: 'Team Efficiency', desc: 'Streamline development workflows and boost productivity' },
  { title: 'Cloud Expertise', desc: 'Build a foundation for rapid innovation' },
  { title: 'Modern Stack', desc: 'Implement standards and best practices at scale' },
]

export default function LeadDataEngWorkshopPage() {
  return (
    <div className="min-h-screen bg-kinetic-surface text-kinetic-on-surface">
      {/* Hero */}
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-24 sm:px-12 lg:px-16">
        <div className="flex flex-col gap-12 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl flex-1">
            <h1 className="font-headline text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">
              Lead the Future of Data Products
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-kinetic-on-surface-variant">
              Master Backstage.io and transform your organisation&apos;s data platform in 5 days.
            </p>
            <div className="mt-8">
              <Link href="/workshops/register">
                <Button variant="kinetic" size="lg">
                  Join the Innovation
                </Button>
              </Link>
            </div>
          </div>
          <div className="border-kinetic-outline-variant/30 flex-shrink-0 border bg-kinetic-surface-container-low p-7 lg:w-80">
            <p className="mb-2 font-mono text-[10px] uppercase tracking-wider text-kinetic-on-surface-variant">
              5-Day Intensive
            </p>
            <p className="font-headline text-3xl font-bold text-white">$2,999</p>
            <p className="mt-2 text-sm text-kinetic-on-surface-variant">
              Virtual (Live) · Leadership
            </p>
            <Link href="/workshops/register" className="mt-4 block">
              <Button variant="kinetic" size="lg" className="w-full">
                Reserve Your Spot
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="mx-auto max-w-7xl px-6 pb-16 sm:px-12 lg:px-16">
        <h2 className="mb-8 font-headline text-2xl font-bold uppercase tracking-tight text-white">
          Take Your Leadership to the Next Level
        </h2>
        <div className="space-y-4">
          {benefits.map(b => (
            <div key={b} className="flex items-start gap-4">
              <div className="mt-0.5 h-6 w-6 flex-shrink-0 rounded-full bg-kinetic-primary-container" />
              <p className="leading-relaxed text-kinetic-on-surface-variant">{b}</p>
            </div>
          ))}
        </div>
        <Link href="/workshops/register" className="mt-8 inline-block">
          <Button variant="kinetic" size="lg">
            Reserve Your Spot Now
          </Button>
        </Link>
      </section>

      {/* Curriculum */}
      <section className="mx-auto max-w-7xl px-6 pb-16 sm:px-12 lg:px-16">
        <h2 className="mb-8 font-headline text-2xl font-bold uppercase tracking-tight text-white">
          Your Data Product Innovation Journey
        </h2>
        <div className="space-y-4">
          {curriculum.map(item => (
            <div
              key={item.day}
              className="border-kinetic-outline-variant/30 border bg-kinetic-surface-container-low p-6"
            >
              <h3 className="mb-3 font-headline text-base font-semibold text-white">{item.day}</h3>
              <ul className="space-y-2">
                {item.topics.map(t => (
                  <li
                    key={t}
                    className="flex items-center gap-3 text-sm text-kinetic-on-surface-variant"
                  >
                    <div className="h-1.5 w-1.5 rounded-full bg-kinetic-primary-container" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <Link href="/workshops/register" className="mt-8 inline-block">
          <Button variant="kinetic" size="lg">
            Reserve Your Spot Now
          </Button>
        </Link>
      </section>

      {/* Features */}
      <section className="border-kinetic-outline-variant/30 border-t bg-kinetic-surface-container-low py-16">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-16">
          <h2 className="mb-12 text-center font-headline text-2xl font-bold uppercase tracking-tight text-white">
            Investment In Your Future
          </h2>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {features.map(f => (
              <div
                key={f.title}
                className="border-kinetic-outline-variant/30 border bg-kinetic-surface-container-lowest p-6 text-center"
              >
                <div className="mx-auto mb-4 h-12 w-12 bg-kinetic-primary-container" />
                <h3 className="font-headline text-lg font-semibold text-white">{f.title}</h3>
                <p className="mt-2 text-sm text-kinetic-on-surface-variant">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 py-16 text-center sm:px-12 lg:px-16">
        <h2 className="font-headline text-2xl font-bold uppercase tracking-tight text-white">
          Ready to Lead Data Innovation?
        </h2>
        <p className="mx-auto mt-2 max-w-md text-base text-kinetic-on-surface-variant">
          Join other leaders making the leap to modern data product platforms.
        </p>
        <Link href="/workshops/register" className="mt-6 inline-block">
          <Button variant="kinetic" size="lg">
            Enroll Now
          </Button>
        </Link>
      </section>

      <KineticWorkshopFooter />
    </div>
  )
}
